import { NO_ERRORS_SCHEMA,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  AfterViewInit,
  ContentChild,
  TemplateRef,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
  inject,
  ChangeDetectionStrategy } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort, SortDirection } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule, MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { CommonModule } from '@angular/common';
import { CdkTableModule } from '@angular/cdk/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';

export interface ColumnDefinition {
  name: string;
  header: string;
  sortable?: boolean;
  width?: number;
  expandedWidth?: number;
  sortKey?: string;
  expandable?: boolean;
  isHyperlink?: boolean;
  toggleExpand?: boolean;
  checkbox?: boolean;
  action?: boolean;
  centered?: boolean;
  type?: string;
}

// ExpandableRow defines optional properties for table row expansion functionality.
// Used as an intersection type to add expansion capabilities to any data type without
// requiring explicit index signatures or inheritance from data types.
export interface ExpandableRow {
  expanded?: boolean;
  subCells?: unknown[];
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'cfi-mat-table',
  templateUrl: './cfi-mat-table.component.html',
  styleUrls: ['./cfi-mat-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    CdkTableModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule
  ],
  schemas: [NO_ERRORS_SCHEMA]
})
export class CfiMatTableComponent<T = object> implements AfterViewInit, OnInit, OnChanges, OnDestroy {
  @Input() serverSidePaging: boolean = false;
  @Input() borderless: boolean = false;
  @Input()
  set data(value: T[]) {
    this._data = value ?? [];
  }

  get data(): T[] {
    return this._data;
  }
  @Input() paginated: boolean = true;
  @Input() columnDefinitions: ColumnDefinition[] = [];
  @Input() pageSizeOptions: number[] = [5, 10, 25];
  @Input() defaultPageSize: number = 10;
  @Input() hidePageSizeSelector: boolean = true;
  @Input() totalRows: number;
  @Input() currentPageIndex: number = 0;
  @Input() currentPageSize: number = 10;
  @Input() displayedColumns: string[] = [];
  @Input() columnsToDisplayWithExpand: string[] = [];
  @Input() expandedElement: T | null = null;
  @Input() striped: boolean = true;
  @Input() showHeader: boolean = true;
  @Input() scrollable: boolean = false;
  @Input() allowExpand: boolean = true;
  @Input() rowClass: ((row: T) => string) | null = null;
  @Input() sortKey: string = 'lastName';
  @Input() sortDirection: SortDirection = 'asc';
  @Output() expandedElementChange = new EventEmitter<T | null>();
  @Output() cellButtonClicked = new EventEmitter<T>();
  @Output() hyperlinkClicked = new EventEmitter<{ row: T; column: string }>();
  @Output() cbPaginate = new EventEmitter<PageEvent>();
  @Output() cbSort = new EventEmitter<{ active: string; direction: SortDirection }>();

  selectedCell: T | null = null;

  get hasActionColumn(): boolean {
    return this.columnDefinitions.some(
      (column) => column.action === true && this.displayedColumns.includes(column.name)
    );
  }

  get scrollHeaderColumns(): ColumnDefinition[] {
    const columnsByName = new Map(this.columnDefinitions.map((column) => [column.name, column]));
    return this.displayedColumns
      .map((name) => columnsByName.get(name))
      .filter((column): column is ColumnDefinition => !!column);
  }

  @ContentChild('cellTemplate') cellTemplate!: TemplateRef<Record<string, unknown>>;
  @ContentChild('headerTemplate') headerTemplate!: TemplateRef<Record<string, unknown>>;
  @ContentChild('expandedRow') expandedRow!: TemplateRef<Record<string, unknown>>;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  dataSource = new MatTableDataSource<T>([]);

  private _data: T[] = [];
  private onChangesTimeoutId?: ReturnType<typeof setTimeout>;
  private afterViewInitTimeoutId?: ReturnType<typeof setTimeout>;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  constructor() {
    this.isExpanded = this.isExpanded.bind(this);
    this.isExpandedRow = this.isExpandedRow.bind(this);
    this.hasSubCells = this.hasSubCells.bind(this);
    this.getSubCells = this.getSubCells.bind(this);
    this.toggleRow = this.toggleRow.bind(this);
  }

  isExpanded(row: T): boolean {
    if (!row) return false;
    return this.expandedElement === row;
  }

  isExpandedRow(index: number, row: T): boolean {
    return this.isExpanded(row);
  }

  hasSubCells(row: T): boolean {
    if (!row) return false;
    const expandableRow = row as T & Partial<ExpandableRow>;
    return Array.isArray(expandableRow.subCells) && expandableRow.subCells.length > 0;
  }

  getSubCells(row: T): unknown[] {
    if (!row) return [];
    const expandableRow = row as T & Partial<ExpandableRow>;
    return expandableRow.subCells || [];
  }

  toggleRow(row: T, event?: Event): void {
    const expandableRow = row as T & Partial<ExpandableRow>;
    if (!row || expandableRow.subCells == undefined) return;

    if (event) {
      event.stopPropagation();
    }

    if (this.expandedElement === row) {
      this.expandedElement = null;
      expandableRow.expanded = false;
    } else {
      this.expandedElement = row;
      expandableRow.expanded = true;
    }

    this.expandedElementChange.emit(this.expandedElement);
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.initializeTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['columnDefinitions'] || changes['expandedElement']) {
      this.initializeTable();
    }

    if (changes['totalRows']) {
      this.updatePaginatorLength();
    }

    if (changes['currentPageIndex'] || changes['currentPageSize']) {
      this.updatePaginatorState();
    }

    if (this.paginated && this.paginator) {
      this.onChangesTimeoutId = setTimeout(() => {
        this.updatePaginatorState();
        this.cdr.markForCheck();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.onChangesTimeoutId);
    clearTimeout(this.afterViewInitTimeoutId);
  }

  private initializeTable(): void {
    if (!this.data) return;

    this.dataSource = new MatTableDataSource<T>([...this.data]);

    if (!this.displayedColumns.length) {
      this.displayedColumns = this.columnDefinitions.map((col) => col.name);
    }

    if (!this.columnsToDisplayWithExpand.length) {
      this.columnsToDisplayWithExpand = [...this.displayedColumns];
    }

    this.validateColumnDefinitions();

    this.setupDataSource();

    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    this.setupDataSource();
    this.afterViewInitTimeoutId = setTimeout(() => {
      this.updatePaginatorLength();
      this.updatePaginatorState();
      this.cdr.markForCheck();
    }, 0);
  }

  private setupDataSource(): void {
    // Setup custom sorting data accessor first
    this.setupSortingDataAccessor();

    // Sorting: attach MatSort only when NOT using server-side paging
    if (this.sort) {
      if (this.serverSidePaging) {
        this.dataSource.sort = null as unknown as MatSort; // detach to ignore client sorting
      } else {
        // Apply custom sort for contact tables (keeps primary contact at top regardless of sort column)
        if (this.data.length > 0 && 'isPrimaryContact' in (this.data[0] as object)) {
          this.customSortForContacts();
        }
        this.dataSource.sort = this.sort;
      }
    }

    if (this.paginated && this.paginator) {
      if (this.serverSidePaging) {
        this.dataSource.paginator = null;
      } else {
        this.dataSource.paginator = this.paginator;
      }
      this.updatePaginatorLength();
      this.updatePaginatorState();
      this.paginatorIntl.itemsPerPageLabel = 'Rows per page:';
      this.cdr.markForCheck();
    }
  }

  private updatePaginatorLength(): void {
    if (this.paginated && this.paginator && this.totalRows !== undefined) {
      const len = Number(this.totalRows ?? 0);
      this.paginator.length = Number.isFinite(len) ? len : 0;
      if (this.paginator.pageIndex * this.paginator.pageSize >= this.paginator.length) {
        this.paginator.pageIndex = 0;
        this.currentPageIndex = 0;
      }
      this.cdr.markForCheck();
    }
  }

  private updatePaginatorState(): void {
    if (this.paginated && this.paginator) {
      const pageIndex = Number(this.currentPageIndex ?? 0);
      const pageSize = Number(this.currentPageSize ?? this.paginator.pageSize);
      const coercedLength = Number(this.totalRows ?? this.paginator.length ?? 0);

      if (this.paginator.pageIndex !== pageIndex) {
        this.paginator.pageIndex = Number.isFinite(pageIndex) ? pageIndex : 0;
      }
      if (this.paginator.pageSize !== pageSize) {
        this.paginator.pageSize = Number.isFinite(pageSize) ? pageSize : this.paginator.pageSize;
      }
      if (this.paginator.length !== coercedLength && Number.isFinite(coercedLength)) {
        this.paginator.length = coercedLength;
      }
      this.cdr.markForCheck();
    }
  }

  private setupSortingDataAccessor(): void {
    this.dataSource.sortingDataAccessor = (item: T, property: string): string | number => {
      const column = this.columnDefinitions.find((col) => col.name === property);
      if (!column?.sortable) return '';

      const value = column.sortKey ? this.getNestedValue(item, column.sortKey) : item[property];

      // Normalize strings for case-insensitive sorting
      if (typeof value === 'string') {
        return value.toLocaleLowerCase();
      }

      if (Array.isArray(value)) {
        return value
          .map((v) => (typeof v === 'string' ? v : (v?.name ?? '')))
          .join(', ')
          .toLocaleLowerCase();
      }
      return value || '';
    };
  }

  private getNestedValue(item: T, path: string): string | number {
    const value = path.split('.').reduce<unknown>((obj, key) => (obj as Record<string, unknown>)?.[key], item);
    if (Array.isArray(value)) {
      return value.map((v) => v.name).join(', ');
    }
    return (value as string | number) || '';
  }

  private validateColumnDefinitions() {
    if (!this.displayedColumns || !this.columnDefinitions) {
      console.warn('CfiMatTable: displayedColumns and columnDefinitions must be provided');
      return;
    }

    const columnNames = this.columnDefinitions.map((col) => col.name);
    const missingColumns = this.displayedColumns.filter((col) => !columnNames.includes(col));

    if (missingColumns.length > 0) {
      console.warn(
        `CfiMatTable: The following columns are missing from columnDefinitions: ${missingColumns.join(', ')}`
      );
    }

    const extraColumns = columnNames.filter((col) => !this.displayedColumns.includes(col));
    if (extraColumns.length > 0) {
      console.warn(
        `CfiMatTable: The following columnDefinitions are not in displayedColumns: ${extraColumns.join(', ')}`
      );
    }
  }

  selectCell(cell: T): void {
    this.selectedCell = cell;
    this.cdr.markForCheck();
  }

  onCellButtonClick(row: T): void {
    this.cellButtonClicked.emit(row);
  }

  onHyperlinkClick(row: T, column: string): void {
    this.hyperlinkClicked.emit({ row, column });
  }

  handleCellButtonClicked(cell: T): void {
    this.cellButtonClicked.emit(cell);
  }

  onPageFired(event: PageEvent): void {
    this.currentPageIndex = event.pageIndex;
    this.currentPageSize = event.pageSize;
    this.cbPaginate.emit(event);
    this.cdr.markForCheck();
  }

  onSortChange(event: Sort): void {
    this.cbSort.emit({ active: event.active, direction: event.direction });
  }

  // Custom sortData: always keep primary contact at the top (sort by alphabetical order with firstname)
  // regardless of sort direction or sort state
  private customSortForContacts(): void {
    this.dataSource.sortData = (data, sort) => {
      const activeSort = sort.active === 'isPrimaryContact' ? 'firstName' : sort.active;
      const primary = data.find((c) => c['isPrimaryContact']);
      const others = data.filter((c) => !c['isPrimaryContact']);

      if (!activeSort || !sort.direction) {
        return primary ? [primary, ...others] : others;
      }

      // Sort others by the active sort key (default to firstName if not present)
      const direction = sort.direction === 'desc' ? -1 : 1;

      const sortedOthers = [...others].sort((a, b) => {
        const aValue = this.dataSource.sortingDataAccessor(a, activeSort);
        const bValue = this.dataSource.sortingDataAccessor(b, activeSort);

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * direction;
        }

        return (
          String(aValue).localeCompare(String(bValue), undefined, {
            sensitivity: 'base'
          }) * direction
        );
      });

      return primary ? [primary, ...sortedOthers] : sortedOthers;
    };
  }
}
